import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  Users, 
  Building2, 
  Settings, 
  Wrench, 
  Calendar,
  User,
  LogOut,
  Menu,
  X,
  Sliders,
  Package,
  AlertTriangle,
  Plus,
  UserCog,
  ClipboardList,
  History,
  BarChart3,
  List
} from 'lucide-react';
import { useAuth } from '@shared/authContext';
import { canAccessAdvancedSettings } from '@shared/permissions';
import { useClientModal } from '@shared/clientModalContext';
import ClientFormModal from './ClientFormModal';
import PartsNeededFormModal from './PartsNeededFormModal';
import { partsNeededAPI, clientsAPI } from '@shared/apiClient';

const navigation = [
  { name: 'Regular Service', href: '/regular-service', icon: Home },
  // { name: 'Repair Service', href: '/priority-service', icon: AlertTriangle }, // Hidden - functionality moved to Regular Service "All Clients" tab
  { name: 'Tech Assignments', href: '/tech-assignments', icon: UserCog },
  { name: 'Parts Needed', href: '/parts-needed', icon: ClipboardList, badgeKey: 'parts' },
  { name: 'Users', href: '/users', icon: Users },
  { name: 'Customers', href: '/clients', icon: Building2, badgeKey: 'pendingClients' },
  { name: 'Service History', href: '/service-history', icon: History },
  { name: 'Monthly Roster', href: '/monthly-roster', icon: List },
  { name: 'Service Calls', href: '/service-calls', icon: Calendar },
  { name: 'Tech Metrics', href: '/tech-metrics', icon: BarChart3 },
  { name: 'Inventory', href: '/inventory', icon: Package },
  { name: 'Advanced Settings', href: '/advanced-settings', icon: Sliders },
];

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const { openCreateModal, isOpen, client, mode, closeModal } = useClientModal();
  const [isPartsModalOpen, setIsPartsModalOpen] = useState(false);

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gray-50">
      {/* Fixed Header - Full Width with Blue Gradient */}
      <header className="flex-shrink-0 h-16 bg-gradient-to-r from-primary-500 to-primary-600 shadow-lg z-30 flex items-center">
        <button
          type="button"
          className="px-4 text-white focus:outline-none lg:hidden"
          onClick={() => setSidebarOpen(true)}
        >
          <Menu className="h-6 w-6" />
        </button>
        <div className="flex-1 px-6 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Wrench className="h-7 w-7 text-white" />
            <div>
              <span className="text-lg font-bold text-white">
                K&R Services
              </span>
              <span className="text-xs text-blue-100 ml-2 font-medium">
                Admin Dashboard
              </span>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={openCreateModal}
              className="flex items-center justify-center h-10 w-10 rounded-full bg-white text-primary-600 hover:bg-blue-50 transition-all shadow-md hover:shadow-lg transform hover:scale-105"
              title="Quick Add Customer"
            >
              <Plus className="h-6 w-6" />
            </button>
            <button
              onClick={() => setIsPartsModalOpen(true)}
              className="flex items-center justify-center h-10 w-10 rounded-full bg-white text-primary-600 hover:bg-blue-50 transition-all shadow-md hover:shadow-lg transform hover:scale-105"
              title="Request Part"
            >
              <ClipboardList className="h-6 w-6" />
            </button>
            <span className="text-sm text-white font-medium">
              {user?.firstName} {user?.lastName}
            </span>
            <button
              onClick={logout}
              className="flex items-center text-sm text-blue-100 hover:text-white transition-colors"
            >
              <LogOut className="h-4 w-4 mr-1" />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Mobile sidebar overlay */}
      <div className={`fixed inset-0 z-40 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white h-full shadow-xl">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white transition-colors"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-6 w-6 text-white" />
            </button>
          </div>
          <SidebarContent />
        </div>
      </div>

      {/* Main Content Area with Sidebars */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Fixed with Blue Accent */}
        <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:border-r lg:border-primary-200 bg-white shadow-sm">
          <SidebarContent />
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>

      {/* Global Client Form Modal */}
      <ClientFormModal
        client={client}
        isOpen={isOpen}
        onClose={closeModal}
        mode={mode}
      />

      <PartsNeededFormModal
        isOpen={isPartsModalOpen}
        onClose={() => setIsPartsModalOpen(false)}
        mode="create"
        part={null}
      />
    </div>
  );
}

function SidebarContent() {
  const location = useLocation();
  const { user } = useAuth();
  const { data: partsSummary } = useQuery(
    ['partsNeededSummary'],
    () => partsNeededAPI.getPartsNeededSummary(),
    { refetchInterval: 60000 }
  );
  const { data: pendingClientsData } = useQuery(
    ['pendingClients'],
    () => clientsAPI.getPendingClients(),
    { refetchInterval: 60000 }
  );

  const openPartsCount = useMemo(() => partsSummary?.data?.openCount ?? 0, [partsSummary]);
  const pendingClientsCount = useMemo(() => pendingClientsData?.data?.clients?.length ?? 0, [pendingClientsData]);

  const badgeCounts = useMemo(() => ({
    parts: openPartsCount,
    pendingClients: pendingClientsCount
  }), [openPartsCount, pendingClientsCount]);

  const filteredNavigation = useMemo(
    () =>
      navigation.filter(
        (item) =>
          item.href !== '/advanced-settings' || canAccessAdvancedSettings(user)
      ),
    [user]
  );

  return (
    <div className="flex-1 flex flex-col overflow-y-auto">
      <div className="flex-1 flex flex-col pt-5 pb-4">
        <div className="flex items-center flex-shrink-0 px-4 mb-6">
          <div className="flex items-center space-x-2">
            <div className="h-8 w-1 bg-primary-600 rounded-r"></div>
            <h1 className="text-xl font-bold text-gray-900">Navigation</h1>
          </div>
        </div>
        <nav className="flex-1 px-2 space-y-1">
          {filteredNavigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all ${
                  isActive
                    ? 'bg-primary-600 text-white shadow-md'
                    : 'text-gray-600 hover:bg-primary-50 hover:text-primary-900'
                }`}
              >
                <item.icon
                  className={`mr-3 flex-shrink-0 h-5 w-5 ${
                    isActive ? 'text-white' : 'text-gray-400 group-hover:text-primary-600'
                  }`}
                />
                <span className="flex-1">{item.name}</span>
                {item.badgeKey && badgeCounts[item.badgeKey] > 0 && (
                  <span
                    className={`ml-2 inline-flex items-center justify-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                      isActive ? 'bg-white text-primary-700' : 'bg-primary-100 text-primary-700'
                    }`}
                  >
                    {badgeCounts[item.badgeKey]}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
      </div>
      <div className="flex-shrink-0 flex border-t border-primary-100 p-4 bg-gradient-to-r from-gray-50 to-blue-50">
        <Link
          to="/profile"
          className="flex-shrink-0 w-full group block hover:bg-white rounded-lg p-2 transition-colors"
        >
          <div className="flex items-center">
            <div className="bg-primary-100 p-2 rounded-full group-hover:bg-primary-200 transition-colors">
              <User className="inline-block h-5 w-5 text-primary-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-700 group-hover:text-primary-900">
                Profile Settings
              </p>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
}
